import React, { useState, useRef, useEffect } from 'react';
import { 
  X, 
  MessageSquare, 
  Send, 
  Minus, 
  User, 
  Mail, 
  Phone, 
  FileText, 
  Bot, 
  Check, 
  ArrowRight, 
  Sparkles,
  Shield,
  Database,
  Cpu,
  AlertTriangle,
  Clock,
  Calendar,
  AlertCircle,
  TicketIcon
} from 'lucide-react';
import axios from 'axios';
import { ticketService, aiService } from '../services/api';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ChatSupportBot = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState('ai'); // 'ai' or 'support'
  const [messages, setMessages] = useState([
    { 
      type: 'bot', 
      content: "Hello! I'm your AI security assistant powered by vector search. I can help answer questions about your scan results, findings, threat intelligence, and provide security recommendations based on your data.", 
      timestamp: new Date() 
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });
  const [supportSubmitted, setSupportSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [sessionId, setSessionId] = useState(`session_${Date.now()}`);
  const [sources, setSources] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState([
    "What vulnerabilities were found in my latest scan?",
    "How do I fix SQL injection vulnerabilities?",
    "What are the best security practices for my network?",
    "How often should I run security scans?"
  ]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [faqs, setFaqs] = useState([]);
  const [showFaqs, setShowFaqs] = useState(false);
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  
  // Add state for authentication status
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Check authentication status on component mount
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      setIsAuthenticated(!!token);
    };
    
    checkAuth();
    // Set up event listener for storage changes (login/logout)
    window.addEventListener('storage', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);
  
  // Fetch FAQs on component mount
  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get('/api/chat/faqs');
        if (response.data.faqs) {
          setFaqs(response.data.faqs);
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      }
    };
    
    fetchFaqs();
  }, []);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // Toggle chat bot visibility
  const toggleChat = () => {
    setIsOpen(!isOpen);
    setMinimized(false);
  };
  
  // Toggle minimized state
  const toggleMinimize = (e) => {
    e.stopPropagation();
    setMinimized(!minimized);
  };
  
  // Handle chat input changes
  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };
  
  // Handle support form changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setSupportForm({
      ...supportForm,
      [name]: value
    });
    
    // Clear error for this field when typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  // Generate dynamic suggestions based on user messages
  const generateSuggestions = (latestBotMessage) => {
    const content = latestBotMessage.toLowerCase();
    let newSuggestions = [];
    
    if (content.includes('vulnerability') || content.includes('vulnerabilities')) {
      newSuggestions = [
        "How do I fix these vulnerabilities?",
        "What's the risk level of these issues?",
        "Show me recommendations for these issues",
        "How often should I scan for vulnerabilities?"
      ];
    } else if (content.includes('scan results') || content.includes('recent scan')) {
      newSuggestions = [
        "Show me the details of my latest scan",
        "What vulnerabilities were found?",
        "Are there any critical issues?",
        "When should I run another scan?"
      ];
    } else if (content.includes('recommendation') || content.includes('best practice')) {
      newSuggestions = [
        "Tell me more about network security",
        "How can I improve data protection?",
        "What authentication methods do you recommend?",
        "How can I protect against ransomware?"
      ];
    } else {
      newSuggestions = [
        "What vulnerabilities were found in my latest scan?",
        "What are the best security practices?",
        "How do I fix SQL injection vulnerabilities?",
        "I need help with my security issues"
      ];
    }
    
    setSuggestedQuestions(newSuggestions);
    setShowSuggestions(true);
  };
  
  // Handle AI chat message send
  const handleSendMessage = async (e, suggestedMessage = null) => {
    if (e) e.preventDefault();
    
    // Use suggested message if provided, otherwise use input message
    const messageToSend = suggestedMessage || inputMessage;
    
    if (!messageToSend.trim()) return;

    if (!user) {
      toast.error('Please log in to use the AI assistant');
      return;
    }

    setLoading(true);
    const userMessage = messageToSend.trim();
    setInputMessage('');
    setShowSuggestions(false);

    // Add user message to chat
    setMessages(prev => [...prev, { 
      type: 'user', 
      content: userMessage, 
      timestamp: new Date() 
    }]);

    try {
      // Send message to new AI endpoint
      const response = await aiService.chat(userMessage, sessionId);
      
      if (response.success && response.data) {
        // Update session ID if provided
        if (response.data.sessionId) {
          setSessionId(response.data.sessionId);
        }
        
        // Store sources for display
        if (response.data.sources && response.data.sources.length > 0) {
          setSources(response.data.sources);
        }
        
        // Add AI response to chat
        const botResponse = {
          type: 'bot',
          content: response.data.response,
          timestamp: new Date(),
          sources: response.data.sources || []
        };
        
        setMessages(prev => [...prev, botResponse]);
        
        // Generate new suggestions based on bot response
        generateSuggestions(response.data.response);
        
        // Show success toast if sources were found
        if (response.data.sources && response.data.sources.length > 0) {
          toast.success(`Found ${response.data.sources.length} relevant sources`);
        }
      } else {
        throw new Error(response.message || 'Failed to get response from AI');
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = error.response?.data?.message || 
                      error.message ||
                      'Sorry, I encountered an error. Please try again later.';
      console.error('Error details:', errorMsg);
      
      setMessages(prev => [...prev, { 
        type: 'bot',
        content: errorMsg,
        timestamp: new Date(),
        isError: true
      }]);
      
      toast.error('Failed to get AI response. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle suggested question click
  const handleSuggestedQuestionClick = (question) => {
    handleSendMessage(null, question);
  };
  
  // Toggle FAQs visibility
  const toggleFaqs = () => {
    setShowFaqs(!showFaqs);
  };
  
  // Handle support form submission
  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    if (!supportForm.name.trim()) errors.name = 'Name is required';
    if (!supportForm.email.trim()) errors.email = 'Email is required';
    if (!supportForm.description.trim()) errors.description = 'Description is required';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setLoading(true);
    
    try {
      // Create ticket using ticket service
      const ticketData = {
        title: `Support Request: ${supportForm.category}`,
        description: supportForm.description,
        priority: supportForm.priority.toUpperCase(),
        category: ({
          'general': 'GENERAL',
          'technical': 'TECHNICAL',
          'billing': 'BILLING',
          'feature_request': 'FEATURE_REQUEST',
          'bug': 'BUG',
          'other': 'OTHER'
        })[supportForm.category] || 'TECHNICAL'
      };
      
      const response = await ticketService.createTicket(ticketData);
      
      setSupportSubmitted(true);
      setMessages(prev => [...prev, {
        type: 'bot',
        content: `Your support ticket has been created successfully. Ticket ID: ${response.ticket._id}. You can view and manage your tickets in the Support Tickets section.`,
        timestamp: new Date()
      }]);
      
      // Show link to tickets page
      setMessages(prev => [...prev, {
        type: 'bot',
        content: 'linkToTickets',
        timestamp: new Date()
      }]);
      
      resetSupportForm();
      setActiveTab('ai');
    } catch (error) {
      console.error('Error submitting support request:', error);
      
      setMessages(prev => [...prev, {
        type: 'bot',
        content: 'Sorry, there was an error creating your support ticket. Please try again later.',
        timestamp: new Date(),
        isError: true
      }]);
      
      toast.error('Failed to create support ticket');
    } finally {
      setLoading(false);
    }
  };
  
  // Reset support form
  const resetSupportForm = () => {
    setSupportForm({
      name: '',
      email: '',
      phone: '',
      description: '',
      priority: 'medium',
      category: 'general'
    });
    setSupportSubmitted(false);
    setFormErrors({});
  };
  
  // Handle key press (Enter to send)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSendMessage(e);
    }
  };
  
  // Modify the renderMessage function to handle the special message type
  const renderMessage = (message, index) => {
    if (message.type === 'bot' && message.content === 'linkToTickets') {
      return (
        <div 
          key={index} 
          className={`flex items-start mb-4 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div className="max-w-xs sm:max-w-md lg:max-w-lg bg-blue-500 text-white p-4 rounded-lg">
            <Link 
              to="/tickets" 
              className="flex items-center justify-center gap-2 text-white font-medium bg-blue-600 hover:bg-blue-700 transition duration-200 px-4 py-2 rounded"
            >
              <TicketIcon className="h-4 w-4" />
              View My Tickets
            </Link>
          </div>
        </div>
      );
    }
    
    // Regular message rendering (existing code)
    return (
      <div 
        key={index} 
        className={`flex items-start mb-4 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
      >
        {message.type === 'bot' && (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-md mr-2">
            <Bot className="text-white w-5 h-5" />
          </div>
        )}
        
        <div 
          className={`max-w-xs sm:max-w-md lg:max-w-lg p-3 rounded-lg ${
            message.type === 'user' 
              ? 'bg-blue-600 text-white rounded-br-none' 
              : message.isError 
                ? 'bg-red-100 text-red-800' 
                : 'bg-gray-100 text-gray-800 rounded-bl-none'
          }`}
        >
          <div className="whitespace-pre-wrap">{message.content}</div>
          <div className="text-xs opacity-70 mt-1 text-right">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        
        {message.type === 'user' && (
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center shadow-md ml-2">
            <User className="text-white w-5 h-5" />
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end">
      {/* Chat Bot Icon */}
      {!isOpen && (
        <button 
          onClick={toggleChat}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 animate-bounce"
          aria-label="Open chat support"
        >
          <MessageSquare className="h-6 w-6" />
        </button>
      )}
      
      {/* Chat Bot Window */}
      {isOpen && (
        <div 
          className={`bg-white rounded-xl shadow-2xl border border-gray-200 transition-all duration-300 ${
            minimized ? 'h-16 w-72' : 'w-80 sm:w-96'
          }`}
          style={{ 
            maxHeight: minimized ? '4rem' : '600px',
            height: minimized ? '4rem' : '500px'
          }}
        >
          {/* Header */}
          <div 
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-t-xl flex justify-between items-center cursor-pointer"
            onClick={!minimized ? () => {} : toggleChat}
          >
            <div className="flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              <h3 className="font-medium">Security Assistant</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={toggleMinimize} 
                className="text-white hover:text-blue-100 focus:outline-none transition-colors"
                aria-label={minimized ? "Expand chat" : "Minimize chat"}
              >
                <Minus className="h-5 w-5" />
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleChat();
                }} 
                className="text-white hover:text-blue-100 focus:outline-none transition-colors"
                aria-label="Close chat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {!minimized && (
            <>
              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                <button
                  className={`flex-1 py-2 px-4 font-medium transition-colors ${
                    activeTab === 'ai' 
                      ? 'text-blue-600 border-b-2 border-blue-600' 
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                  onClick={() => setActiveTab('ai')}
                >
                  <div className="flex items-center justify-center">
                    <Sparkles className="h-4 w-4 mr-2" />
                    AI Assistant
                  </div>
                </button>
                <button
                  className={`flex-1 py-2 px-4 font-medium transition-colors ${
                    activeTab === 'support' 
                      ? 'text-blue-600 border-b-2 border-blue-600' 
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                  onClick={() => setActiveTab('support')}
                >
                  <div className="flex items-center justify-center">
                    <User className="h-4 w-4 mr-2" />
                    Support
                  </div>
                </button>
              </div>
              
              {/* Content */}
              <div className="h-[calc(100%-8rem)] overflow-hidden">
                {activeTab === 'ai' ? (
                  // AI Chat Interface
                  <div className="flex flex-col h-full">
                    <div 
                      ref={chatContainerRef}
                      className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar"
                    >
                      {/* FAQ Button */}
                      <div className="flex justify-center mb-2">
                        <button
                          onClick={toggleFaqs}
                          className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded-full flex items-center hover:bg-blue-100 transition-colors"
                        >
                          {showFaqs ? "Hide FAQs" : "Show Security FAQs"}
                          <AlertCircle className="h-3 w-3 ml-1" />
                        </button>
                      </div>
                      
                      {/* FAQs Panel */}
                      {showFaqs && (
                        <div className="bg-blue-50 p-3 rounded-lg mb-3 text-sm">
                          <h4 className="font-medium text-blue-700 mb-2">Frequently Asked Questions</h4>
                          <div className="space-y-2 max-h-40 overflow-y-auto p-1">
                            {faqs.map((faq, index) => (
                              <div key={index} className="bg-white p-2 rounded shadow-sm">
                                <p className="font-medium text-gray-800 cursor-pointer" 
                                   onClick={() => handleSendMessage(null, faq.question)}>
                                  {faq.question}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Chat Messages */}
                      {messages.map((message, index) => (
                        renderMessage(message, index))
                      )}
                      
                      {/* Loading indicator */}
                      {loading && (
                        <div className="flex justify-start animate-fadeIn">
                          <div className="max-w-[80%] bg-gray-100 rounded-lg p-3 rounded-bl-none">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Suggested Questions */}
                      {showSuggestions && !loading && (
                        <div className="flex flex-wrap gap-2 justify-center mt-2">
                          {suggestedQuestions.map((question, index) => (
                            <button
                              key={index}
                              onClick={() => handleSuggestedQuestionClick(question)}
                              className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-xs hover:bg-blue-100 transition-colors"
                            >
                              {question}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      <div ref={messagesEndRef} />
                    </div>
                    
                    {/* Input Area */}
                    <div className="p-3 border-t border-gray-200">
                      <div className="relative">
                        <input
                          type="text"
                          value={inputMessage}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPress}
                          placeholder="Ask about your security results..."
                          className="w-full py-2 pl-4 pr-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={loading}
                        />
                        <button
                          onClick={(e) => handleSendMessage(e)}
                          disabled={!inputMessage.trim() || loading}
                          className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${
                            !inputMessage.trim() || loading
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-blue-600 hover:text-blue-800'
                          }`}
                        >
                          <Send className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="text-xs text-gray-500 mt-2 flex items-center">
                        <Database className="h-3 w-3 mr-1" />
                        <span>AI has access to your scan results and security data</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Support Form
                  <div className="p-4 h-full overflow-y-auto custom-scrollbar">
                    {!supportSubmitted ? (
                      <form onSubmit={handleSupportSubmit} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <User className="h-4 w-4 mr-1 text-blue-500" />
                            Your Name
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={supportForm.name}
                            onChange={handleFormChange}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              formErrors.name ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="John Doe"
                          />
                          {formErrors.name && (
                            <p className="mt-1 text-xs text-red-500">{formErrors.name}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <Mail className="h-4 w-4 mr-1 text-blue-500" />
                            Email Address
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={supportForm.email}
                            onChange={handleFormChange}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              formErrors.email ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="example@company.com"
                          />
                          {formErrors.email && (
                            <p className="mt-1 text-xs text-red-500">{formErrors.email}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <Phone className="h-4 w-4 mr-1 text-blue-500" />
                            Phone Number (Optional)
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={supportForm.phone}
                            onChange={handleFormChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <AlertTriangle className="h-4 w-4 mr-1 text-blue-500" />
                              Priority
                            </label>
                            <select
                              name="priority"
                              value={supportForm.priority}
                              onChange={handleFormChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="critical">Critical</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <FileText className="h-4 w-4 mr-1 text-blue-500" />
                              Category
                            </label>
                            <select
                              name="category"
                              value={supportForm.category}
                              onChange={handleFormChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="general">General</option>
                              <option value="technical">Technical</option>
                              <option value="billing">Billing</option>
                              <option value="feature_request">Feature Request</option>
                              <option value="bug">Bug Report</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <FileText className="h-4 w-4 mr-1 text-blue-500" />
                            Description of Issue
                          </label>
                          <textarea
                            name="description"
                            value={supportForm.description}
                            onChange={handleFormChange}
                            rows="4"
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              formErrors.description ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Please describe your issue in detail..."
                          ></textarea>
                          {formErrors.description && (
                            <p className="mt-1 text-xs text-red-500">{formErrors.description}</p>
                          )}
                        </div>
                        
                        {formErrors.submit && (
                          <div className="bg-red-50 text-red-800 p-3 rounded-md text-sm">
                            {formErrors.submit}
                          </div>
                        )}
                        
                        <div className="flex justify-end">
                          <button
                            type="submit"
                            disabled={loading}
                            className={`px-4 py-2 rounded-md text-white font-medium flex items-center ${
                              loading 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg transform transition-all duration-200 hover:-translate-y-1'
                            }`}
                          >
                            {loading ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                Submitting...
                              </>
                            ) : (
                              <>
                                Submit Request 
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    ) : (
                      // Success message after submission
                      <div className="flex flex-col items-center justify-center h-full text-center animate-fadeIn">
                        <div className="bg-green-100 p-3 rounded-full mb-4">
                          <Check className="h-8 w-8 text-green-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-800 mb-2">Support Request Submitted!</h3>
                        <p className="text-sm text-gray-600 mb-6">
                          Thank you for contacting us. We'll get back to you as soon as possible.
                        </p>
                        <button
                          onClick={() => {
                            resetSupportForm();
                            setActiveTab('ai');
                          }}
                          className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md font-medium hover:bg-blue-200 transition-colors"
                        >
                          Return to AI Assistant
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Footer attribution */}
              <div className="p-2 text-center border-t border-gray-200">
                <p className="text-xs text-gray-500 flex items-center justify-center">
                  <Cpu className="h-3 w-3 mr-1" />
                  Powered by SecureAI
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatSupportBot; 